import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronUp, Download, Plus, Send, Trash2 } from 'lucide-react';
import { useNotification } from '@iziping/ui';
import type { SurveyQuestion, SurveyTemplate } from '@iziping/common';
import Toggle from '../../../components/shared/Toggle';
import ConfirmationModal from '../../../components/shared/ConfirmationModal';
import {
    useDeleteSurveyTemplate,
    useExportSurveyResults,
    useGetSurveyResults,
    useGetSurveyTemplate,
    useSaveSurveyTemplate,
    useSendSurvey,
} from '../hooks/surveyHooks';
import styles from './TournamentSurveyConfig.module.css';

interface Props {
    tournamentId: number;
    allHeatsFinished: boolean;
}

const EMPTY_TEMPLATE: SurveyTemplate = { enabled: false, questions: [] };

const newQuestion = (order: number): SurveyQuestion => ({ label: '', type: 'RATING', order });

const TournamentSurveyConfig: React.FC<Props> = ({ tournamentId, allHeatsFinished }) => {
    const { t } = useTranslation();
    const { notify } = useNotification();

    const { data, isLoading } = useGetSurveyTemplate(tournamentId);
    const save = useSaveSurveyTemplate(tournamentId);
    const deleteTemplate = useDeleteSurveyTemplate(tournamentId);
    const sendSurvey = useSendSurvey(tournamentId);
    const exportResults = useExportSurveyResults(tournamentId);

    const [template, setTemplate] = useState<SurveyTemplate>(EMPTY_TEMPLATE);
    const hasResponses = (data?.responseCount ?? 0) > 0;
    const { data: results } = useGetSurveyResults(tournamentId, hasResponses);
    const [showQuestions, setShowQuestions] = useState(true);
    const [expandedQuestions, setExpandedQuestions] = useState<Set<number>>(new Set());
    const [showDisableModal, setShowDisableModal] = useState(false);
    const [showSendModal, setShowSendModal] = useState(false);

    const TEXT_PREVIEW = 5;
    const toggleExpand = (questionId: number) =>
        setExpandedQuestions((prev) => {
            const next = new Set(prev);
            next.has(questionId) ? next.delete(questionId) : next.add(questionId);
            return next;
        });

    useEffect(() => {
        if (data) setShowQuestions((data.responseCount ?? 0) === 0);
    }, [data]);

    useEffect(() => {
        if (data) setTemplate(data);
    }, [data]);

    const handleToggle = (enabled: boolean) => {
        if (!enabled && template.enabled) {
            setShowDisableModal(true);
        } else {
            setTemplate((t) => ({ ...t, enabled }));
        }
    };

    const handleConfirmDisable = async () => {
        try {
            const updated = { ...template, enabled: false };
            await save.mutateAsync(updated);
            setTemplate(updated);
            notify(t('tournament.survey.delete.success'), 'success');
        } catch (error: any) {
            notify(t(error.error) || t('internal.server.error'), 'error');
        } finally {
            setShowDisableModal(false);
        }
    };

    const handleSave = async (e: React.FormEvent) => {
        e.preventDefault();
        if (template.questions.some((q) => !q.label.trim())) {
            notify(t('tournament.survey.question.label.required'), 'error');
            return;
        }
        try {
            await save.mutateAsync(template);
            notify(t('tournament.survey.save.success'), 'success');
        } catch (error: any) {
            notify(t(error.error) || t('internal.server.error'), 'error');
        }
    };

    const handleSendSurvey = async () => {
        try {
            await sendSurvey.mutateAsync();
            notify(t('tournament.survey.send.success'), 'success');
        } catch (error: any) {
            notify(t(error.error) || t('internal.server.error'), 'error');
        } finally {
            setShowSendModal(false);
        }
    };

    const addQuestion = () => {
        setTemplate((prev) => ({
            ...prev,
            questions: [...prev.questions, newQuestion(prev.questions.length)],
        }));
    };

    const removeQuestion = (index: number) => {
        setTemplate((prev) => ({
            ...prev,
            questions: prev.questions.filter((_, i) => i !== index).map((q, i) => ({ ...q, order: i })),
        }));
    };

    const updateQuestion = (index: number, patch: Partial<SurveyQuestion>) => {
        setTemplate((prev) => ({
            ...prev,
            questions: prev.questions.map((q, i) => (i === index ? { ...q, ...patch } : q)),
        }));
    };

    const moveQuestion = (index: number, direction: -1 | 1) => {
        const target = index + direction;
        if (target < 0 || target >= template.questions.length) return;
        setTemplate((prev) => {
            const qs = [...prev.questions];
            [qs[index], qs[target]] = [qs[target], qs[index]];
            return { ...prev, questions: qs.map((q, i) => ({ ...q, order: i })) };
        });
    };

    if (isLoading) return <div>{t('common.loading')}</div>;

    return (
        <div className={styles.wrapper}>
            <div>
                <h3>{t('tournament.survey.section.title')}</h3>
                <p className={styles.description}>{t('tournament.survey.section.description')}</p>
            </div>

            <Toggle
                checked={template.enabled}
                onChange={handleToggle}
                label={template.enabled ? t('tournament.survey.enabled') : t('tournament.survey.disabled')}
            />

            <ConfirmationModal
                isOpen={showDisableModal}
                title={t('tournament.survey.disable.confirm.title')}
                message={t('tournament.survey.disable.confirm.message')}
                confirmLabel={t('tournament.survey.disable.confirm.button')}
                onConfirm={handleConfirmDisable}
                onCancel={() => setShowDisableModal(false)}
            />

            <ConfirmationModal
                isOpen={showSendModal}
                title={t('tournament.survey.send.confirm.title')}
                message={t('tournament.survey.send.confirm.message', { count: template.eligibleCount ?? '?' })}
                confirmLabel={t('tournament.survey.send.confirm.button')}
                onConfirm={handleSendSurvey}
                onCancel={() => setShowSendModal(false)}
            />

            {hasResponses && results && (
                <div className={styles.results}>
                    <div className={styles.resultsTitleRow}>
                        <h4 className={styles.resultsTitle}>{t('tournament.survey.results.title')}</h4>
                        {results.questions.some((q) => q.type === 'TEXT') && (
                            <button
                                type="button"
                                className={styles.exportBtn}
                                onClick={() => exportResults.mutate()}
                                disabled={exportResults.isPending}
                            >
                                <Download size={14} />
                                {t('tournament.survey.results.export')}
                            </button>
                        )}
                    </div>
                    <p className={styles.resultsParticipation}>
                        {t('tournament.survey.results.participation', {
                            count: results.responseCount,
                            total: results.eligibleCount,
                            pct:
                                results.eligibleCount > 0
                                    ? Math.round((results.responseCount / results.eligibleCount) * 100)
                                    : 0,
                        })}
                    </p>
                    {results.questions.map((q) => {
                        const texts = q.texts ?? [];
                        const expanded = expandedQuestions.has(q.questionId);
                        const visible = expanded ? texts : texts.slice(0, TEXT_PREVIEW);
                        const remaining = texts.length - TEXT_PREVIEW;
                        return (
                            <div key={q.questionId} className={styles.resultBlock}>
                                <p className={styles.resultLabel}>{q.label}</p>
                                {q.type === 'RATING' ? (
                                    <div className={styles.ratingResult}>
                                        <span className={styles.ratingAvg}>{q.averageRating?.toFixed(1)}</span>
                                        <div className={styles.ratingStars}>
                                            {[1, 2, 3, 4, 5].map((s) => (
                                                <span
                                                    key={s}
                                                    className={`${styles.resultStar} ${s <= Math.round(q.averageRating ?? 0) ? styles.resultStarActive : ''}`}
                                                >
                                                    ★
                                                </span>
                                            ))}
                                        </div>
                                        <span className={styles.ratingMax}>/5</span>
                                    </div>
                                ) : (
                                    <>
                                        <ul className={styles.textAnswers}>
                                            {texts.length === 0 ? (
                                                <li className={styles.textAnswerEmpty}>
                                                    {t('tournament.survey.results.no_text')}
                                                </li>
                                            ) : (
                                                visible.map((text, i) => (
                                                    <li key={i} className={styles.textAnswer}>
                                                        {text}
                                                    </li>
                                                ))
                                            )}
                                        </ul>
                                        {remaining > 0 && (
                                            <button
                                                type="button"
                                                className={styles.expandBtn}
                                                onClick={() => toggleExpand(q.questionId)}
                                            >
                                                {expanded
                                                    ? t('tournament.survey.results.collapse')
                                                    : t('tournament.survey.results.expand', { count: remaining })}
                                            </button>
                                        )}
                                    </>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            {template.enabled && (
                <>
                    {hasResponses && (
                        <button
                            type="button"
                            className={styles.questionsToggle}
                            onClick={() => setShowQuestions((v) => !v)}
                        >
                            {showQuestions ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
                            {t('tournament.survey.questions.toggle')}
                        </button>
                    )}
                    {showQuestions && (
                        <form onSubmit={handleSave} className={styles.questionList}>
                            {template.questions.length === 0 && (
                                <p className={styles.emptyState}>{t('tournament.survey.questions.empty')}</p>
                            )}

                            {template.questions.map((q, i) => (
                                <div key={i} className={styles.questionRow}>
                                    <span className={styles.questionIndex}>{i + 1}</span>

                                    <input
                                        className={styles.questionLabel}
                                        value={q.label}
                                        onChange={(e) => updateQuestion(i, { label: e.target.value })}
                                        placeholder={t('tournament.survey.question.label.placeholder')}
                                    />

                                    <div className={styles.typeToggle}>
                                        <button
                                            type="button"
                                            className={`${styles.typeBtn} ${q.type === 'RATING' ? styles.typeBtnActive : ''}`}
                                            onClick={() => updateQuestion(i, { type: 'RATING' })}
                                            disabled={hasResponses && !!q.id}
                                        >
                                            ★ {t('tournament.survey.type.rating')}
                                        </button>
                                        <button
                                            type="button"
                                            className={`${styles.typeBtn} ${q.type === 'TEXT' ? styles.typeBtnActive : ''}`}
                                            onClick={() => updateQuestion(i, { type: 'TEXT' })}
                                            disabled={hasResponses && !!q.id}
                                        >
                                            ✍ {t('tournament.survey.type.text')}
                                        </button>
                                    </div>

                                    <button
                                        type="button"
                                        className={styles.iconBtn}
                                        onClick={() => moveQuestion(i, -1)}
                                        disabled={i === 0}
                                        title={t('tournament.survey.question.move.up')}
                                    >
                                        <ChevronUp size={15} />
                                    </button>
                                    <button
                                        type="button"
                                        className={styles.iconBtn}
                                        onClick={() => moveQuestion(i, 1)}
                                        disabled={i === template.questions.length - 1}
                                        title={t('tournament.survey.question.move.down')}
                                    >
                                        <ChevronDown size={15} />
                                    </button>
                                    <button
                                        type="button"
                                        className={`${styles.iconBtn} ${styles.iconBtnDanger}`}
                                        onClick={() => removeQuestion(i)}
                                        disabled={hasResponses && !!q.id}
                                        title={t('common.delete')}
                                    >
                                        <Trash2 size={15} />
                                    </button>
                                </div>
                            ))}

                            {!hasResponses && (
                                <button type="button" className={styles.addButton} onClick={addQuestion}>
                                    <Plus size={15} />
                                    {t('tournament.survey.question.add')}
                                </button>
                            )}

                            <div className={styles.actions}>
                                <button type="submit" className="button" disabled={save.isPending}>
                                    {save.isPending ? '...' : t('tournament.survey.save.button')}
                                </button>

                                {allHeatsFinished &&
                                    template.questions.length > 0 &&
                                    (template.sent ? (
                                        <span className={styles.sentBadge}>
                                            <Send size={13} />
                                            {t('tournament.survey.send.already_sent')}
                                        </span>
                                    ) : (
                                        <button
                                            type="button"
                                            className={styles.sendButton}
                                            disabled={sendSurvey.isPending}
                                            onClick={() => setShowSendModal(true)}
                                        >
                                            <Send size={15} />
                                            {sendSurvey.isPending
                                                ? '...'
                                                : t('tournament.survey.send.button', {
                                                      count: template.eligibleCount ?? '?',
                                                  })}
                                        </button>
                                    ))}
                            </div>
                        </form>
                    )}
                </>
            )}
        </div>
    );
};

export default TournamentSurveyConfig;
